import { useEffect, useState } from "react";
import { Plus, Minus, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Seo from "@/components/Seo";
import PageHero from "@/components/site/PageHero";
import { getFaqs, getPage } from "@/lib/api";

export default function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [page, setPage] = useState(null);

  useEffect(() => {
    getFaqs().then(setFaqs).catch(() => {});
    getPage("faq").then(setPage);
  }, []);

  const filtered = faqs.filter((f) => f.question.toLowerCase().includes(q.toLowerCase()) || f.answer.toLowerCase().includes(q.toLowerCase()));
  const cats = [...new Set(filtered.map((f) => f.category))];

  const schema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
  };

  return (
    <>
      <Seo
        title={page?.seo_title || "FAQ | Legacy India Travel"}
        description={page?.seo_description || "Frequently asked questions about India tours, visas, halal food, best time to visit, safety and more — for travellers from around the world."}
        schema={schema}
      />
      <PageHero
        title={page?.hero_title || "Frequently Asked Questions"}
        subtitle={page?.hero_subtitle || "Everything you need to know before your India journey."}
        image={page?.hero_image || "https://images.unsplash.com/photo-1564507592333-c60657eea523?auto=format&fit=crop&w=1600&q=80"}
        crumbs={[{ label: "FAQ" }]}
      />

      <section className="py-16 sm:py-24 bg-[#fdfbf7]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          {page?.intro && (
            <p className="text-[#5a4b4b] text-lg leading-relaxed mb-8 whitespace-pre-line" data-testid="faq-intro">{page.intro}</p>
          )}
          <div className="flex items-center gap-2 border border-[#d4af37]/40 bg-white px-4 py-3 mb-10">
            <Search className="w-4 h-4 text-[#7c1d1d]" />
            <input data-testid="faq-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions..." className="flex-1 bg-transparent outline-none text-sm text-[#3b1a1a]" />
          </div>

          {cats.map((cat) => (
            <div key={cat} className="mb-10">
              <h2 className="font-serif text-2xl text-[#7c1d1d] mb-2">{cat}</h2>
              <div className="border-t border-[#d4af37]/25">
                {filtered.filter((f) => f.category === cat).map((f) => (
                  <div key={f.id} className="border-b border-[#d4af37]/25">
                    <button data-testid={`faq-${f.id}`} onClick={() => setOpen(open === f.id ? null : f.id)} className="w-full flex items-center justify-between gap-4 py-5 text-left group">
                      <span className="font-serif text-xl text-[#3b1a1a] group-hover:text-[#7c1d1d] transition-colors">{f.question}</span>
                      <span className="shrink-0 w-8 h-8 border border-[#7c1d1d]/40 grid place-items-center text-[#7c1d1d]">{open === f.id ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</span>
                    </button>
                    <AnimatePresence initial={false}>
                      {open === f.id && <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden"><p className="pb-5 text-[#5a4b4b] leading-relaxed">{f.answer}</p></motion.div>}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center py-16 text-[#5a4b4b]">No questions match your search.</div>}
        </div>
      </section>
    </>
  );
}
